import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { cn } from "@/shared/lib/utils"
import { getAverageMetrics } from "@/shared/api/statistics"

import { AHTDistributionChart } from "./AHTDistributionChart"
import { CalculationVolumeChart } from "./CalculationVolumeChart"

type StatisticsPanelProps = {
  className?: string
}

const FALLBACK_METRICS: { labelKey: string; valueKey: string; value: number }[] = [
  {
    labelKey: "statistics.average.handleTime.label",
    valueKey: "statistics.average.handleTime.value",
    value: 340,
  },
  {
    labelKey: "statistics.average.serviceLevel.label",
    valueKey: "statistics.average.serviceLevel.value",
    value: 67,
  },
  {
    labelKey: "statistics.average.answerTime.label",
    valueKey: "statistics.average.answerTime.value",
    value: 30,
  },
  {
    labelKey: "statistics.average.shrinkage.label",
    valueKey: "statistics.average.shrinkage.value",
    value: 32.5,
  },
  {
    labelKey: "statistics.average.occupancy.label",
    valueKey: "statistics.average.occupancy.value",
    value: 84.2,
  },
]

export function StatisticsPanel({ className }: StatisticsPanelProps) {
  const { t } = useTranslation()
  const [averageMetrics, setAverageMetrics] = useState(FALLBACK_METRICS)

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const metrics = await getAverageMetrics()

        if (metrics) {
          const updatedMetrics = [
            {
              labelKey: "statistics.average.handleTime.label",
              valueKey: "statistics.average.handleTime.value",
              value: Math.round(metrics.handleTime),
            },
            {
              labelKey: "statistics.average.serviceLevel.label",
              valueKey: "statistics.average.serviceLevel.value",
              value: Math.round(metrics.serviceLevel),
            },
            {
              labelKey: "statistics.average.answerTime.label",
              valueKey: "statistics.average.answerTime.value",
              value: Math.round(metrics.answerTime),
            },
            {
              labelKey: "statistics.average.shrinkage.label",
              valueKey: "statistics.average.shrinkage.value",
              value: Math.round(metrics.shrinkage * 10) / 10,
            },
            {
              labelKey: "statistics.average.occupancy.label",
              valueKey: "statistics.average.occupancy.value",
              value: Math.round(metrics.occupancy * 10) / 10,
            },
          ]
          setAverageMetrics(updatedMetrics)
        }
      } catch (error) {
        console.error("Error fetching average metrics:", error)
        // Keep fallback data on error
      }
    }

    fetchMetrics()
  }, [])

  return (
    <aside className={cn("w-full stack-lg", className)}>
      <Card aria-labelledby="calculation-volume-heading">
        <CardHeader className="stack-sm text-left">
          <CardTitle id="calculation-volume-heading">{t("statistics.volume.title")}</CardTitle>
          <CardDescription className="body-xs pb-2">
            {t("statistics.volume.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-12">
          <CalculationVolumeChart />
        </CardContent>
      </Card>

      <Card aria-labelledby="average-values-heading">
        <CardContent className="stack-md text-[var(--color-text-secondary)]">
          <section aria-labelledby="average-values-heading" className="stack-md">
            <div className="stack-xs">
              <h3 className="heading-sm" id="average-values-heading">
                {t("statistics.average.title")}
              </h3>
              <p className="body-xs">{t("statistics.average.description")}</p>
            </div>
            <dl className="stack-md">
              {averageMetrics.map((metric) => (
                <div className="flex items-start justify-between gap-6" key={metric.labelKey}>
                  <dt className="body-sm font-medium text-[var(--color-text-primary)]">
                    {t(metric.labelKey)}
                  </dt>
                  <dd className="heading-sm text-right text-[var(--color-text-primary)]">
                    {t(metric.valueKey, { value: metric.value })}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        </CardContent>
      </Card>

      <Card aria-labelledby="handle-time-distribution">
        <CardHeader className="stack-sm text-left">
          <CardTitle className="heading-sm mb-4" id="handle-time-distribution">
            {t("statistics.aht.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <AHTDistributionChart />
        </CardContent>
      </Card>
    </aside>
  )
}
